import { range } from "lodash";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { BuildingModel, CommonFacilityModel, SpaceStyle } from "src/api/building/building-types";
import { BuildingFacilityTagModel } from "src/api/building/commonfacility-types";
import { MediaFile } from "src/api/public-types";
import { BaseButton, BaseInput, BaseSelect, BaseTextarea } from "src/components";
import PublicImageUpdate from "src/components/PublicImageUpdate";
import TagChip from "src/components/TagChip";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import { useQueryParams } from "src/hooks/useQueryParams";
import { SelectOption, floorOptions, spaceStyleOptions } from "src/pages/building/building-types";
import { useModal } from "src/recoil/modalState/hook";
import FacilityTagSelectList from "../components/FacilityTagSelectList";

const requiredMessage = "필수입력 항목입니다.";

type Props = {
  building?: BuildingModel;
  onClose: () => void;
  handleConfirm: (passData?: CommonFacilityModel) => void;
  facility?: CommonFacilityModel;
  readonly?: boolean;
  onChageReadOnly: () => void;
};

const CommonSpaceBasicInfo = ({ building, onClose, handleConfirm, facility, readonly, onChageReadOnly }: Props) => {
  const {
    queryParams: { facilityType },
  } = useQueryParams();

  const isMeetingRoom = useMemo(() => {
    return facilityType === "MEETING_ROOM";
  }, [facilityType]);

  const { isAuthority } = usePartnerAuthority();
  const { setAbstractModalZ1 } = useModal();

  const rangeFloorOptions: Array<SelectOption> = useMemo(() => {
    // 건물 > 기본정보 > 규모 범위
    const floorRange = range(
      Number(building?.undergroundFloorNums || 0) === 0 ? 0 : Number(building?.undergroundFloorNums || 0) * -1,
      Number(building?.floorNums || 0) + 1,
    );
    const _rangeFloorOptions: Array<SelectOption> = [];
    for (let i = 0; i < floorRange.length; i++) {
      const floor: number = floorRange[i];
      if (floor !== 0) {
        _rangeFloorOptions.push({
          value: String(floor || ""),
          label: floorOptions.find((v: SelectOption) => v.value === String(floor || ""))?.label || "",
        });
      }
    }
    if (_rangeFloorOptions.length === 0) {
      return floorOptions;
    } else {
      return _rangeFloorOptions.sort((a: SelectOption, b: SelectOption) => Number(b.value || 0) - Number(a.value || 0));
    }
  }, [building]);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CommonFacilityModel>({
    defaultValues: {
      facilityName: "",
      spaceStyle: "SPACE_STYLE_UNRECOGNIZED",
      locationCode: "",
      equipments: "",
      description: "",
      mediaList: [],
    },
  });

  // 등록 form 태그 목록 setValue 관리
  const tagsForm = useForm<{ facilityTagList: BuildingFacilityTagModel[] }>({
    defaultValues: {
      facilityTagList: [],
    },
  });

  useEffect(() => {
    if (facility) {
      setValue("id", facility.id);
      setValue("floorNum", facility.isGround ? facility.floorNum : -(facility.floorNum || 0));
      setValue("facilityName", facility.facilityName);
      setValue("spaceStyle", facility.spaceStyle);
      setValue("locationCode", facility.locationCode);
      setValue("facilityMaxPeopleNums", facility.facilityMaxPeopleNums);
      setValue("description", facility.description);
      setValue("equipments", facility.facilityList?.join(","));
      setValue("mediaList", facility.mediaList || []);

      // 태그 목록 setValue 관리
      tagsForm.setValue("facilityTagList", facility.facilityTagList || []);
    }
  }, [facility]);

  // 태그 선택 모달 오픈
  const openTagSelectModal = async () => {
    setAbstractModalZ1({
      isOpen: true,
      size: "large",
      children: (
        <FacilityTagSelectList
          onClose={() => setAbstractModalZ1({ isOpen: false })}
          selectedIdList={tagsForm.getValues("facilityTagList").map((tag) => String(tag.id))}
          onSelectList={(selectedTagList: BuildingFacilityTagModel[]) => {
            const currentTagList = tagsForm.getValues("facilityTagList");
            const newTagList = [...currentTagList, ...selectedTagList];
            tagsForm.setValue("facilityTagList", newTagList);
          }}
          serviceId={String(facility?.id)}
        />
      ),
    });
  };

  // validation 통과 후 submit 될때 실행
  const onSubmit = async (data: CommonFacilityModel, e?: any) => {
    e.preventDefault();
    e.stopPropagation();

    const isFacilityTagValid = await tagsForm.trigger("facilityTagList");
    if (!isFacilityTagValid) {
      return false;
    }

    const requestData = {
      ...data,
      facilityTagList: tagsForm.getValues("facilityTagList"),
    };

    handleConfirm(requestData);
    e.target.reset();
    return false;
  };

  // validation 통과하지 못하고 error 발생시 실행
  const onError = (errors: any, e?: any) => {
    e.preventDefault();
    console.log("onError errors", errors);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)}>
      <div className="contents-container__wrap px30">
        <section className="contents-container__grid">
          <div className="contents-container__grid-index">
            <p className={!readonly ? "required" : ""}>위치</p>
          </div>
          <div className="contents-container__grid-contents">
            {readonly ? (
              <div className="minmax240">
                <span>
                  {floorOptions.find((option: SelectOption) => option.value === `${facility?.isGround ? "" : "-"}${facility?.floorNum}`)?.label}
                </span>
              </div>
            ) : (
              <div className="minmax240">
                <Controller
                  control={control}
                  name="floorNum"
                  rules={{ required: requiredMessage }}
                  render={({ field: { value, name, onChange }, formState: { errors } }) => (
                    <BaseSelect
                      className=""
                      stateOptions={rangeFloorOptions}
                      setStateValue={onChange}
                      value={value}
                      name={name}
                      errorText={errors.floorNum?.message}
                    />
                  )}
                ></Controller>
              </div>
            )}
          </div>
        </section>

        <section className="contents-container__grid">
          <div className="contents-container__grid-index">
            <p className={!readonly ? "required" : ""}>{isMeetingRoom ? "회의실 명" : "편의시설 명"}</p>
          </div>
          <div className="contents-container__grid-contents">
            {readonly ? (
              <div className="minmax240">
                <span>{watch("facilityName")}</span>
              </div>
            ) : (
              <div className="minmax240">
                <Controller
                  control={control}
                  name="facilityName"
                  rules={{ required: requiredMessage }}
                  render={({ field: { value, name, onChange }, formState: { errors } }) => (
                    <BaseInput
                      type="text" //
                      value={value}
                      name={name}
                      onChange={onChange}
                      placeholder="입력해주세요"
                      errorText={errors.facilityName?.message}
                    />
                  )}
                ></Controller>
              </div>
            )}
          </div>
        </section>

        <section className="contents-container__grid">
          <div className="contents-container__grid-index">
            <p className={!readonly ? "required" : ""}>{isMeetingRoom ? "회의실 유형" : "편의시설 유형"}</p>
          </div>
          <div className="contents-container__grid-contents">
            {readonly ? (
              <div className="minmax240">
                <span>{spaceStyleOptions.find((option: SelectOption) => option.value === watch("spaceStyle"))?.label}</span>
              </div>
            ) : (
              <div className="minmax240">
                <Controller
                  control={control}
                  name="spaceStyle"
                  rules={{
                    validate: {
                      required: (spaceStyle?: SpaceStyle) => {
                        return (!!spaceStyle && spaceStyle !== "SPACE_STYLE_UNRECOGNIZED") || requiredMessage;
                      },
                    },
                  }}
                  render={({ field: { value, name, onChange }, formState: { errors } }) => (
                    <BaseSelect
                      className="minmax240"
                      stateOptions={spaceStyleOptions}
                      setStateValue={onChange}
                      value={value}
                      name={name}
                      errorText={errors.spaceStyle?.message}
                    />
                  )}
                ></Controller>
              </div>
            )}
          </div>
        </section>

        <section className="contents-container__grid">
          <div className="contents-container__grid-index">
            <p className={!readonly ? "required" : ""}>Location code</p>
          </div>
          <div className="contents-container__grid-contents">
            {readonly ? (
              <div className="minmax240">
                <span>{watch("locationCode")}</span>
              </div>
            ) : (
              <div className="minmax240">
                <Controller
                  control={control}
                  name="locationCode"
                  rules={{ required: requiredMessage }}
                  render={({ field: { value, name, onChange }, formState: { errors } }) => (
                    <BaseInput value={value} name={name} onChange={onChange} placeholder="입력해주세요" errorText={errors.locationCode?.message} />
                  )}
                ></Controller>
              </div>
            )}
          </div>
        </section>

        <section className="contents-container__grid">
          <div className="contents-container__grid-index">
            <p className={!readonly ? "required" : ""}>정원</p>
          </div>
          <div className="contents-container__grid-contents">
            {readonly ? (
              <div className="minmax240">
                <span>{watch("facilityMaxPeopleNums")}명</span>
              </div>
            ) : (
              <div className="minmax240">
                <Controller
                  control={control}
                  name="facilityMaxPeopleNums"
                  rules={{
                    required: requiredMessage,
                    pattern: {
                      value: /^[0-9]+$/,
                      message: "숫자만 입력할 수 있습니다.",
                    },
                    min: {
                      value: 1,
                      message: "최소 1명 이상 입력해주세요.",
                    },
                    max: {
                      value: 999,
                      message: "최대 999명까지 입력할 수 있습니다.",
                    },
                  }}
                  render={({ field: { value, name, onChange }, formState: { errors } }) => (
                    <BaseInput
                      type="text"
                      value={value}
                      name={name}
                      onChange={onChange}
                      placeholder="입력해주세요"
                      errorText={errors.facilityMaxPeopleNums?.message}
                    />
                  )}
                ></Controller>
              </div>
            )}
          </div>
        </section>

        {/* 태그 */}
        <section className="contents-container__grid">
          <div className="contents-container__grid-index">
            <p>태그</p>
          </div>
          <div className="contents-container__grid-contents">
            {readonly ? (
              <div>
                {tagsForm.watch("facilityTagList").length > 0 ? (
                  <div className="flex-center ">
                    {tagsForm.watch("facilityTagList").map((tag) => (
                      <div key={tag.id} className="mr5">
                        <TagChip tag={tag} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <span>-</span>
                )}
              </div>
            ) : (
              <div>
                <div className="flex-center">
                  <Controller
                    control={tagsForm.control}
                    name="facilityTagList"
                    render={({ field: { value, name, onChange }, formState: { errors } }) => (
                      <BaseButton title="선택" className="color-white" onClick={openTagSelectModal} />
                    )}
                  ></Controller>
                  <div className="flex-center ml10">
                    {tagsForm.watch("facilityTagList").map((tag) => (
                      <div key={tag.id} className="mr5">
                        <TagChip
                          tag={tag}
                          onClear={(item: BuildingFacilityTagModel) => {
                            const clearTagId = item.id;
                            const newTagList = tagsForm.watch("facilityTagList").filter((tag) => tag.id !== clearTagId);
                            tagsForm.setValue("facilityTagList", newTagList);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="contents-container__grid">
          <div className="contents-container__grid-index">
            <p>시설 안내</p>
          </div>
          <div className="contents-container__grid-contents">
            {readonly ? (
              <div className="pt10">
                <span className="pre-formatted">{watch("equipments") || "-"}</span>
              </div>
            ) : (
              <div>
                <Controller
                  control={control}
                  name="equipments"
                  render={({ field: { value, name, onChange } }) => (
                    <BaseTextarea name={name} value={value} onChange={onChange} placeholder="입력해주세요" maxLength={1000} />
                  )}
                ></Controller>
              </div>
            )}
          </div>
        </section>

        <section className="contents-container__grid">
          <div className="contents-container__grid-index">
            <p>이용 안내</p>
          </div>
          <div className="contents-container__grid-contents">
            <div className="pt10">
              {readonly ? (
                <span className="pre-formatted">{watch("description") || "-"}</span>
              ) : (
                <Controller
                  control={control}
                  name="description"
                  render={({ field: { value, name, onChange } }) => (
                    <BaseTextarea name={name} value={value} onChange={onChange} placeholder="입력해주세요" maxLength={1000} />
                  )}
                ></Controller>
              )}
            </div>
          </div>
        </section>

        <section className="contents-container__grid">
          <div className="contents-container__grid-index">
            <p>이미지 {readonly ? "" : `(${watch("mediaList")?.length || 0}/10)`}</p>
          </div>
          <div className="contents-container__grid-contents">
            <div style={{ width: 795 }}>
              <Controller
                control={control}
                name="mediaList"
                render={({ field: { value, onChange } }) => (
                  <>
                    {readonly && value?.length === 0 ? (
                      <span>-</span>
                    ) : (
                      <PublicImageUpdate
                        className="w-100"
                        fileMaxLength={10}
                        mediaFiles={value}
                        isOnlyView={readonly}
                        categoryName={isMeetingRoom ? "meetingRoom" : "refreshRoom"}
                        mediaServiceType={isMeetingRoom ? "MEDIA_BUILDING_MEETING_ROOM" : "MEDIA_BUILDING_REFRESH_ROOM"}
                        isUsedDescription
                        isUsedRepresentative
                        onChangeMediaFiles={(mediaFiles: MediaFile[]) => {
                          const hasPrimaryImage = mediaFiles.some((file) => file.isPrimary);
                          const medias = mediaFiles.map((file: MediaFile, index: number) => {
                            const additionalMediaFile: MediaFile = {
                              ...file,
                              ...{
                                mediaServiceType: isMeetingRoom ? "MEDIA_BUILDING_MEETING_ROOM" : "MEDIA_BUILDING_REFRESH_ROOM", // 회의실
                                category1: "",
                                isPrimary: hasPrimaryImage ? file.isPrimary : index === 0,
                              },
                            };
                            return additionalMediaFile;
                          });
                          onChange(medias);
                          // setMediaList(medias);
                        }}
                      />
                    )}
                  </>
                )}
              ></Controller>
            </div>
          </div>
        </section>
      </div>

      <div className="flex-center-end pa30">
        <BaseButton type="button" title="취소" className="color-white mr5" onClick={onClose} />
        {isAuthority("w") && (
          <>
            {readonly ? (
              <BaseButton type="button" title={"수정"} onClick={() => onChageReadOnly()} /> //
            ) : (
              <BaseButton type="submit" title={"저장"} />
            )}
          </>
        )}
      </div>
    </form>
  );
};

export default CommonSpaceBasicInfo;
